import React, { useState, useEffect, useMemo } from 'react';
import '../styles/dialog.css';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';
import awsLogo from '../assets/Website/AWS.svg';
import googleLogo from '../assets/cloud-providers/google-cloud-icon.svg';
import azureLogo from '../assets/Website/Microsoft_Azure.svg';
// Cloud provider logos for Providers column
import logoAws from '../assets/icons-new-bucket/aws.svg';
import logoGoogle from '../assets/cloud-providers/google-cloud-icon.svg';
import logoAzure from '../assets/icons-new-bucket/azure.svg';
import logoStorj from '../assets/icons-new-bucket/storj.svg';
import logoOther from '../assets/icons-new-bucket/other.svg';
import logoAkave from '../assets/cloud-providers/akave-logo.png';
import logoFilecoin from '../assets/icons-new-bucket/filecoin.svg';
import {
  Typography,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
  Tabs,
  Tab,
  Box,
  Popover,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Dialog as HeadlessUIDialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { StyledButton, StyledButtonIcon } from '../components/StyledButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import eyeSvg from '../assets/details-bucket/eye.svg';
import deleteSvg from '../assets/details-bucket/delete.svg';
import settingsSvg from '../assets/details-bucket/settings.svg';
import { ApiTypes, HttpError } from '@flashbacktech/flashbackclient/api';
import { useApi } from '../contexts/ApiContext';
import { useAuth, useSelectedWorkspace } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useWithTokenRefresh } from '../utils/api';
import { toastError, toastSuccess, toastRepoCreated, toastErrorCustom } from '../utils/toast';
import { EncryptedKey } from '@flashbacktech/flashbackclient/dist/api/types/storage/storage';
import { buildBreakdownParams, buildTimeSeriesParams, formatV2Series } from '../utils/v2StatsHelpers';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`repo-tabpanel-${index}`}
      aria-labelledby={`repo-tab-${index}`}
      {...other}
    >
      {value === index && <Box className="py-4">{children}</Box>}
    </div>
  );
}

interface RepoUnitInfo extends ApiTypes.RepoUnitInfo {
  master: boolean;
}

// Fonction utilitaire pour formater les bytes en KB, MB, GB, TB selon la taille
const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
};

function StorageRepos() {
  const { apiClient } = useApi();
  const { isAuthenticated, authState } = useAuth();
  const navigate = useNavigate();
  const withTokenRefresh = useWithTokenRefresh();
  
  // Use centralized workspace detection hook
  const selectedWorkspaceId = useSelectedWorkspace();
  const [repos, setRepos] = useState<ApiTypes.StorageRepoWithBuckets[]>([]);
  const [availableUnits, setAvailableUnits] = useState<ApiTypes.StorageUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [repoStats, setRepoStats] = useState<Record<string, any>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [openNewRepo, setOpenNewRepo] = useState(false);
  const [openApiKeys, setOpenApiKeys] = useState(false);
  const [bucketsPopoverAnchor, setBucketsPopoverAnchor] = useState<{
    element: HTMLElement;
    repo: ApiTypes.StorageRepoWithBuckets;
  } | null>(null);
  const [selectedRepo, setSelectedRepo] = useState<ApiTypes.StorageRepoWithBuckets | null>(null);
  // Menu state for row actions dropdown
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRepoId, setMenuRepoId] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState<{ [key: string]: boolean }>({});
  const [tabValue, setTabValue] = useState(0);
  const [editingApiKey, setEditingApiKey] = useState<{
    id: string;
    name: string;
    accessType: ApiTypes.AccessType;
    key: string;
    secret: string;
  } | null>(null);

  // Form state
  const [newRepo, setNewRepo] = useState<
    Partial<ApiTypes.StorageRepo> & { repoUnits: RepoUnitInfo[] }
  >({
    name: '',
    storageType: ApiTypes.StorageType.S3,
    mode: ApiTypes.ModeType.NORMAL,
    repoUnits: [],
  });

  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
  }>({});

  // Local state for reload triggers
  const [reloadTick, setReloadTick] = useState(0);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Daily stats for requests chart
  interface DailyStatItem {
    date: string;
    requests: number;
  }
  const [dailyRequestsData, setDailyRequestsData] = useState<DailyStatItem[]>([]);

  // Listen for workspace changes to trigger reloads
  useEffect(() => {
    // When workspace changes, trigger a reload
    setReloadTick((t) => t + 1);
    // Reset to first page
    setCurrentPage(1);
    // Reset force refresh to allow cache usage for new workspace
    setForceRefresh(false);
  }, [selectedWorkspaceId]);

  // Listen for workspace updates
  useEffect(() => {
    const onWorkspacesUpdated = () => {
      setReloadTick((t) => t + 1);
    };
    window.addEventListener('workspacesUpdated', onWorkspacesUpdated as any);
    return () => {
      window.removeEventListener('workspacesUpdated', onWorkspacesUpdated as any);
    };
  }, []);

  // Listen for global refresh signals (same pattern as StorageUnits)
  useEffect(() => {
    const onBucketsUpdated = () => setReloadTick((t) => t + 1);
    window.addEventListener('bucketsUpdated', onBucketsUpdated);
    return () => window.removeEventListener('bucketsUpdated', onBucketsUpdated);
  }, []);

  // Load daily stats when repos change
  useEffect(() => {
    if (repos.length > 0) {
      loadDailyStats();
    }
  }, [repos]);

  // Load daily stats for the requests chart (last 30 days) via V2 time-series API
  const loadDailyStats = async () => {
    try {
      const wsId = selectedWorkspaceId && selectedWorkspaceId !== 'ALL' ? selectedWorkspaceId : null;
      const params = buildTimeSeriesParams(wsId, null, '30d');

      const response = await withTokenRefresh(() =>
        apiClient.getStorageGatewayStats(params as any)
      ) as any;

      if (response?.success && response.series && Array.isArray(response.series)) {
        const formatted = formatV2Series(response.series, '30d');
        const chartData = formatted.map((point: any) => ({
          date: point.name,
          requests: point.requests || 0,
        }));
        setDailyRequestsData(chartData);
      } else {
        setDailyRequestsData([]);
      }
    } catch (error) {
      console.error('[StorageRepos] Error loading daily stats:', error);
      setDailyRequestsData([]);
    }
  };

  // Helper to fetch repos (filter client-side)
  const fetchReposWithWorkspace = React.useCallback(async (): Promise<{ repos: any[] }> => {
    try {
      const response = await withTokenRefresh(() => apiClient.getStorageRepos(selectedWorkspaceId));

      if (!response.success) {
        toastError('Failed to fetch repositories');
        return { repos: [] };
      }
      const allRepos = response.repos || [];
      return { repos: allRepos };
    } catch (error) {
      return { repos: [] };
    }
  }, [selectedWorkspaceId, authState?.token]);

  // Rafraîchir automatiquement la liste 3 secondes après l'arrivée sur la page (une seule fois)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isAuthenticated) return;

      setLoading(true);
      const initialSelected = selectedWorkspaceId;

      fetchReposWithWorkspace()
        .then((response) => {
          if (response && response.repos) {
            // Ignore stale result if workspace changed during the request
            if (selectedWorkspaceId !== initialSelected) {
              setLoading(false);
              return;
            }

            // L'API filtre déjà côté serveur via workspaceId, pas besoin de filtrer côté client
            const repos = response.repos as any[];
            setRepos(repos);

            // If empty immediately after workspace change, schedule a one-time quick retry
            if (repos.length === 0) {
              setTimeout(() => setReloadTick((t) => t + 1), 400);
            }

            // Load per-repo stats via V2 breakdown
            const wsId = selectedWorkspaceId && selectedWorkspaceId !== 'ALL' ? selectedWorkspaceId : null;
            const bdParams = buildBreakdownParams(wsId, null, '30d');
            if (repos.length > 0) {
              withTokenRefresh(() => apiClient.getStorageGatewayBreakdown('repos', bdParams as any))
                .then((breakdownRes: any) => {
                  if (breakdownRes?.success && breakdownRes.entities) {
                    const statsMap: Record<string, any> = {};
                    breakdownRes.entities.forEach((entity: any) => {
                      statsMap[entity.entityId] = {
                        repoId: entity.entityId,
                        requests: entity.totals?.requests || 0,
                        downloadBytes: entity.totals?.dwlBytes || 0,
                        uploadBytes: entity.totals?.uplBytes || 0,
                      };
                    });
                    setRepoStats(statsMap);
                  }
                })
                .catch((error) => { /* Empty */ })
                .finally(() => setLoading(false));
            } else {
              setRepoStats({});
              setLoading(false);
            }
          } else {
            setRepos([]);
            setRepoStats({});
            setLoading(false);
          }
        })
        .catch((error) => {
          setLoading(false);
        });
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Fetch per-repo stats via V2 breakdown (storage + AI in two calls)
    const fetchProviderStats = async (reposList: any[]) => {
      if (!reposList.length) return;

      try {
        const wsId = selectedWorkspaceId && selectedWorkspaceId !== 'ALL' ? selectedWorkspaceId : null;
        const bdParams = buildBreakdownParams(wsId, null, '30d');

        const [storageRes, aiRes] = await Promise.all([
          withTokenRefresh(() => apiClient.getStorageGatewayBreakdown('repos', bdParams as any)).catch(() => null),
          withTokenRefresh(() => apiClient.getAiGatewayBreakdown('repos', bdParams as any)).catch(() => null),
        ]);

        const statsMap: Record<string, any> = {};
        reposList.forEach((repo) => {
          statsMap[repo.id] = {
            repoId: repo.id,
            requests: 0,
            downloadBytes: 0,
            uploadBytes: 0,
          };
        });

        if ((storageRes as any)?.success && (storageRes as any)?.entities) {
          (storageRes as any).entities.forEach((entity: any) => {
            if (statsMap[entity.entityId]) {
              statsMap[entity.entityId].requests += entity.totals?.requests || 0;
              statsMap[entity.entityId].downloadBytes = entity.totals?.dwlBytes || 0;
              statsMap[entity.entityId].uploadBytes = entity.totals?.uplBytes || 0;
            }
          });
        }

        if ((aiRes as any)?.success && (aiRes as any)?.entities) {
          (aiRes as any).entities.forEach((entity: any) => {
            if (statsMap[entity.entityId]) {
              statsMap[entity.entityId].requests += entity.totals?.requests || 0;
            }
          });
        }

        if (isMounted) {
          setRepoStats(statsMap);
        }
      } catch (error) {
        console.error('[StorageRepos] Error loading provider stats:', error);
      }
    };

    const loadData = async () => {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }

      const initialSelected = selectedWorkspaceId;

      // Try to load from cache first (unless force refresh)
      const cacheKey = `repos_cache_${initialSelected}`;
      const statsCacheKey = `repos_stats_cache_${initialSelected}`;

      if (!forceRefresh) {
        try {
          const cached = localStorage.getItem(cacheKey);
          const cachedStats = localStorage.getItem(statsCacheKey);

          if (cached && cachedStats) {
            const cacheData = JSON.parse(cached);
            const statsData = JSON.parse(cachedStats);
            const cacheAge = Date.now() - cacheData.timestamp;

            // Use cache if less than 10 minutes old
            if (cacheAge < 10 * 60 * 1000) {
              setRepos(cacheData.repos);
              setRepoStats(statsData.stats);
              setInitialLoadComplete(true);
              return;
            }
          }
        } catch (error) { /* Ignore errors */ }
      }

      setLoading(true);
      setInitialLoadComplete(false);

      try {
        // Fetch repos and buckets (buckets non-blocking)
        // CRITICAL: Pass workspaceId to API call
        const workspaceIdParam = selectedWorkspaceId && selectedWorkspaceId !== 'ALL' ? selectedWorkspaceId : undefined;
        const bucketsPromise = withTokenRefresh(() => apiClient.getStorageBuckets(workspaceIdParam)).catch(
          () => null as any
        );
        const [reposResponse, bucketsResponse] = await Promise.all([
          fetchReposWithWorkspace(),
          bucketsPromise,
        ]);

        if (!isMounted) return;

        // Buckets disponibles
        if (bucketsResponse?.buckets) setAvailableUnits(bucketsResponse.buckets);
        else setAvailableUnits([]);

        // L'API filtre déjà côté serveur via workspaceId
        const repos = reposResponse?.repos || [];

        // Stale guard
        if (selectedWorkspaceId !== initialSelected) {
          setLoading(false);
          return;
        }

        setRepos(repos);

        // Stats - Calculate requests by summing all providers
        if (repos.length > 0) {
          await fetchProviderStats(repos);
        } else {
          setRepoStats({});
        }

        // Save to cache
        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              repos: repos,
              timestamp: Date.now(),
            })
          );
          localStorage.setItem(
            statsCacheKey,
            JSON.stringify({
              stats: repoStats,
              timestamp: Date.now(),
            })
          );
        } catch (error) { /* Ignore errors */ }

        // Mark initial load as complete
        if (isMounted) {
          setInitialLoadComplete(true);
          setForceRefresh(false);
        }
      } catch (error) {
        if (isMounted) {
          setRepos([]);
          setAvailableUnits([]);
          setRepoStats({});
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          setInitialLoadComplete(true);
        }
      }
    };

    const loadRepos = async () => {
      await loadData();
    };

    loadRepos();

    return () => {
      isMounted = false;
    };
  }, [selectedWorkspaceId, reloadTick, forceRefresh]);

  // Filter, sort and paginate repos
  const { paginatedRepos, totalPages, totalFiltered } = useMemo(() => {
    // Filter by search query
    const filtered = repos.filter((repo) => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        repo.name?.toLowerCase().includes(query) ||
        repo.storageType?.toLowerCase().includes(query) ||
        repo.mode?.toLowerCase().includes(query)
      );
    });

    // Sort by creation date (most recent first)
    const sorted = [...filtered].sort((a, b) => {
      const dateA = (a as any).createdAt || (a as any).created_at || (a as any).timestamp || 0;
      const dateB = (b as any).createdAt || (b as any).created_at || (b as any).timestamp || 0;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });

    // Paginate
    const totalPages = Math.ceil(sorted.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = sorted.slice(startIndex, endIndex);

    return { paginatedRepos: paginated, totalPages, totalFiltered: sorted.length };
  }, [repos, searchQuery, currentPage, itemsPerPage]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const toggleSecretVisibility = (keyId: string) => {
    setShowSecret((prev) => ({
      ...prev,
      [keyId]: !prev[keyId],
    }));
  };

  const validateForm = () => {
    const errors: typeof validationErrors = {};

    if (!newRepo.name?.trim()) {
      errors.name = 'Name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateRepo = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Get workspace ID
      const saved = localStorage.getItem('selectedWorkspaceId') || 'ALL';
      const workspaceIdToUse = saved !== 'ALL' ? saved : selectedWorkspaceId;
      if (!workspaceIdToUse || workspaceIdToUse === 'ALL') {
        throw new Error('Please select a workspace');
      }

      // Créer d'abord le repository sans unités
      const createStorageRepo: ApiTypes.CreateRepoRequest = {
        name: newRepo.name || '',
        storageType: newRepo.storageType || ApiTypes.StorageType.S3,
        mode: newRepo.mode || ApiTypes.ModeType.NORMAL,
        workspaceId: workspaceIdToUse,
        repoUnits: [], // Commencer sans unités pour éviter l'erreur 500
      };

      const response = await withTokenRefresh(() => apiClient.createStorageRepo(createStorageRepo));

      // Afficher la réponse pour comprendre sa structure

      // Si le repository est créé avec succès et qu'il y a des unités à ajouter
      if (response.success && newRepo.repoUnits && newRepo.repoUnits.length > 0) {
        try {
          // Récupérer l'ID du repository créé
          // Comme nous ne connaissons pas la structure exacte, nous vérifions plusieurs possibilités
          const repoId = (response as any).id || (response as any).repoId;

          if (!repoId) {
            throw new Error('Repository ID not found in response');
          }

          // Préparer les unités avec les valeurs correctes
          const repoUnitsToSend = newRepo.repoUnits.map((unit, index) => ({
            unitId: unit.unitId,
            folder: unit.folder || `folder-${Date.now()}-${index}`,
            master: index === 0 ? true : !!unit.master, // S'assurer que la première unité est master
          }));

          // Mettre à jour le repository avec les unités
          const updateRepo: ApiTypes.UpdateRepoRequest = {
            name: newRepo.name || '',
            storageType: newRepo.storageType || ApiTypes.StorageType.S3,
            mode: newRepo.mode || ApiTypes.ModeType.NORMAL,
            workspaceId: workspaceIdToUse,
            repoUnits: repoUnitsToSend,
          };

          await withTokenRefresh(() => apiClient.updateStorageRepo(repoId, updateRepo));
        } catch (updateError) {
          // Même si l'ajout d'unités échoue, le repo a été créé avec succès
          toastSuccess('Repository created, but failed to add storage units');
        }
      }

      if (response.success) {
        // Invalider le cache pour forcer un refresh
        try {
          const cacheKey = `repos_cache_${selectedWorkspaceId}`;
          const statsCacheKey = `repos_stats_cache_${selectedWorkspaceId}`;
          localStorage.removeItem(cacheKey);
          localStorage.removeItem(statsCacheKey);
        } catch (error) { /* Ignore errors */ }

        const reposResp = await fetchReposWithWorkspace();
        // L'API filtre déjà côté serveur via workspaceId
        setRepos(reposResp.repos);
        setOpenNewRepo(false);
        // Reset form
        setNewRepo({
          name: '',
          storageType: ApiTypes.StorageType.S3,
          mode: ApiTypes.ModeType.NORMAL,
          repoUnits: [],
        });
        setValidationErrors({});
        toastRepoCreated();
        // Notify that repo was created (for tutorial)
        window.dispatchEvent(new CustomEvent('repoCreated'));
      }
    } catch (error) {
      if (error instanceof HttpError) {
        switch (error.status) {
          case 400:
            toastErrorCustom('Repo name must be unique');
            break;
          default:
            toastErrorCustom(`Failed to create storage repo: ${error.message}`);
        }
      } else {
        toastErrorCustom('Failed to create storage repo. Please try again.');
      }
    }
  };

  const handleDeleteRepo = async (repoId: string) => {
    try {
      const response = await withTokenRefresh(() => apiClient.deleteStorageRepo(repoId));

      if (response.success) {
        // Invalider le cache pour forcer un refresh
        try {
          const cacheKey = `repos_cache_${selectedWorkspaceId}`;
          const statsCacheKey = `repos_stats_cache_${selectedWorkspaceId}`;
          localStorage.removeItem(cacheKey);
          localStorage.removeItem(statsCacheKey);
        } catch (error) { /* Ignore errors */ }

        const reposResp = await fetchReposWithWorkspace();
        setRepos(reposResp.repos);
        setSelectedRepo(null);
        toastSuccess('Storage repo deleted successfully');
      }
    } catch (error) {
      if (error instanceof HttpError) {
        switch (error.status) {
          case 404:
            toastErrorCustom('Storage repo not found');
            break;
          default:
            toastErrorCustom(`Failed to delete storage repo: ${error.message}`);
        }
      } else {
        toastErrorCustom('Failed to delete storage repo. Please try again.');
      }
    }
  };

  const handleCreateApiKey = async (repoId: string) => {
    try {
      const createRepoKey: ApiTypes.CreateRepoKeyRequest = {
        repoId: repoId,
        name: 'New API Key ' + new Date().toLocaleString(),
        accessType: ApiTypes.AccessType.READ,
      };

      const response = await withTokenRefresh(() => apiClient.createRepoKey(createRepoKey));

      if (response.success) {
        // Immediately offer a download of the key and secret (they won't be fully visible later)
        try {
          const repoMeta = repos.find((r) => r.id === repoId);
          const fileName = `flashback-api-key-${repoMeta?.name || repoId}-${new Date()
            .toISOString()
            .replace(/[:.]/g, '-')}.txt`;
          const content = [
            'Flashback API Credentials (save this file securely)',
            `Repository: ${repoMeta?.name || ''} (${repoId})`,
            `Created: ${new Date().toString()}`,
            '',
            `API Key: ${response.key}`,
            `Secret: ${response.secret || ''}`,
            '',
            'Important: This secret is shown only once. Keep it safe.',
          ].join('\n');
          const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } catch (error) { /* Ignore errors */ }

        // Update the repos state with the new API key
        setRepos((prevRepos) =>
          prevRepos.map((repo) =>
            repo.id === repoId
              ? {
                  ...repo,
                  apiKeys: [
                    ...(repo.apiKeys || []),
                    {
                      id: response.id,
                      name: 'New API Key',
                      accessType: ApiTypes.AccessType.READ,
                      key: response.key,
                      secret: { key: response.secret || '', iv: '', authTag: '', message: '' },
                      createdAt: new Date().toISOString(),
                    },
                  ],
                }
              : repo
          )
        );

        // Set the new key for editing
        const newKey = {
          id: response.id,
          name: 'New API Key',
          accessType: ApiTypes.AccessType.READ,
          key: response.key,
          secret: response.secret || '',
        };
        setEditingApiKey(newKey);
        toastSuccess('API key created successfully');

        // Dispatch event to update quest completion in Sidebar
        window.dispatchEvent(new CustomEvent('repoApiKeyCreated'));
      }
    } catch (error) {
      if (error instanceof HttpError) {
        switch (error.status) {
          case 404:
            toastErrorCustom('Storage repo not found');
            break;
          default:
            toastErrorCustom(`Failed to create API key: ${error.message}`);
        }
      } else {
        toastErrorCustom('Failed to create API key. Please try again.');
      }
    }
  };

  const handleUpdateApiKey = async (
    repoId: string,
    keyId: string,
    name: string,
    accessType: ApiTypes.AccessType
  ) => {
    try {
      const response = await withTokenRefresh(() =>
        apiClient.updateRepoKey(repoId, keyId, { name, accessType })
      );

      if (response.success) {
        const reposResp = await fetchReposWithWorkspace();
        // Appliquer le filtre par workspace courant
        const filteredRepos = (reposResp.repos as any[]).filter((repo: any) => {
          const wid = repo.workspaceId || repo.workspace_id;
          if (!selectedWorkspaceId || selectedWorkspaceId === 'ALL') return true;
          return wid === selectedWorkspaceId;
        });
        setRepos(filteredRepos);
        setEditingApiKey(null);
        toastSuccess('API key updated successfully');
      }
    } catch (error) {
      if (error instanceof HttpError) {
        switch (error.status) {
          case 404:
            toastErrorCustom('API key not found');
            break;
          default:
            toastErrorCustom(`Failed to update API key: ${error.message}`);
        }
      } else {
        toastErrorCustom('Failed to update API key. Please try again.');
      }
    }
  };

  const handleDeleteApiKey = async (repoId: string, keyId: string) => {
    try {
      const response = await withTokenRefresh(() => apiClient.deleteRepoKey(repoId, keyId));

      if (response.success) {
        const reposResp = await fetchReposWithWorkspace();
        // Appliquer le filtre par workspace courant
        const filteredRepos = (reposResp.repos as any[]).filter((repo: any) => {
          const wid = repo.workspaceId || repo.workspace_id;
          if (!selectedWorkspaceId || selectedWorkspaceId === 'ALL') return true;
          return wid === selectedWorkspaceId;
        });
        setRepos(filteredRepos);
        toastSuccess('API key deleted successfully');
      }
    } catch (error) {
      if (error instanceof HttpError) {
        switch (error.status) {
          case 404:
            toastErrorCustom('API key not found');
            break;
          default:
            toastErrorCustom(`Failed to delete API key: ${error.message}`);
        }
      } else {
        toastErrorCustom('Failed to delete API key. Please try again.');
      }
    }
  };

  // Global loader during initial data fetch
  if (loading && !initialLoadComplete) {
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{ minHeight: 'calc(100vh - 200px)' }}
      >
        <div
          className="animate-spin rounded-full h-16 w-16 border-b-2 mb-4"
          style={{ borderColor: '#617BFF' }}
        ></div>
        <p className="text-gray-600 text-lg font-medium">Loading repositories...</p>
        <p className="text-gray-500 text-sm mt-2">Fetching data and statistics</p>
        <p className="text-gray-400 text-xs mt-3 max-w-md text-center">
          First load may take a few moments. Next visits will be instant thanks to caching.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ padding: '24px 40px 24px 40px' }}>
        <Typography
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          Repositories
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
          }}
        >
          Manage your storage repositories and their configurations
        </Typography>
      </div>

      {/* Content */}
      <div className="flex-1 px-10 pb-6 overflow-y-auto">

      {/* Inventory Sub-section Header with buttons */}
      <Box sx={{ ml: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
              Inventory
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#667085' }}>
              View and manage your storage repositories
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Search */}
            {showSearch ? (
              <TextField
                size="small"
                placeholder="Search repositories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onBlur={() => { if (!searchQuery) setShowSearch(false); }}
                autoFocus
                sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '36px' } }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ fontSize: 18, color: '#667085', mr: 0.5 }} />,
                }}
              />
            ) : (
              <Tooltip title="Search">
                <IconButton
                  size="small"
                  onClick={() => setShowSearch(true)}
                  sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
                >
                  <SearchIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {/* Reload */}
            <Tooltip title="Reload">
              <IconButton
                size="small"
                onClick={() => setForceRefresh(true)}
                sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {/* Add */}
            <Tooltip title="Create repository">
              <IconButton
                size="small"
                onClick={() => {
                  setNewRepo({
                    name: '',
                    storageType: ApiTypes.StorageType.S3,
                    mode: ApiTypes.ModeType.NORMAL,
                    repoUnits: [],
                  });
                  setValidationErrors({});
                  setTabValue(0);
                  navigate('/repositories/inventory/add');
                }}
                data-tutorial="add-repo"
                sx={{ width: 36, height: 36, bgcolor: '#617BFF', borderRadius: '8px', color: 'white', '&:hover': { bgcolor: '#4A67E3' } }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <div className="border-b border-gray-200 mb-6"></div>

      {/* Requests Activity Chart (Last 30 Days) */}
      {dailyRequestsData.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#181D27', mb: 2 }}>
            Requests Activity (Last 30 Days)
          </Typography>
          <div style={{ width: '100%', height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyRequestsData}>
                <defs>
                  <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#617BFF" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#617BFF" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11, fill: '#6B7280' }} 
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 11, fill: '#6B7280' }} 
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
                <RechartsTooltip
                  formatter={(value: number) => [value.toLocaleString(), 'Requests']}
                  contentStyle={{ 
                    fontSize: '12px', 
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="requests"
                  stroke="#617BFF"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorRequests)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md mt-8 mb-8 border border-[#E9EAEB]" style={{ overflow: 'visible' }}>
        <table className="w-full table-auto" style={{ overflow: 'visible' }}>
          <thead>
            <tr
              className="border-b border-gray-200"
              style={{ backgroundColor: 'white', height: '40px' }}
            >
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Providers
              </th>
              <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Resources
              </th>
              <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Keys
              </th>
              <th className="hidden xl:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Support
              </th>
              <th className="hidden xl:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Requests
              </th>
              <th className="hidden xl:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" style={{ overflow: 'visible' }}>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-3 text-gray-700 text-center">
                  Loading storage repositories...
                </td>
              </tr>
            ) : repos.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-3 text-gray-500 text-center">
                  No storage repositories found
                </td>
              </tr>
            ) : (
              paginatedRepos.map((repo: ApiTypes.StorageRepoWithBuckets) => (
                <tr
                  key={repo.id}
                  className="hover:bg-gray-50 cursor-pointer transition-all"
                  style={{ height: '56px' }}
                  onClick={() => {
                    navigate(`/repositories/inventory/${repo.id}`);
                  }}
                >
                  {/* Name Column */}
                  <td className="px-6 py-3 whitespace-nowrap">
                    <Typography variant="body1" style={{ color: '#181D27' }}>
                      {repo.name}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#717680' }}>
                      #{repo.id.substring(0, 6)}
                    </Typography>
                  </td>

                  {/* Providers Column - Overlapping round logos (same style as RepoOverview) */}
                  <td className="hidden md:table-cell px-6 py-3 whitespace-nowrap">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {(() => {
                        // Collect all providers: storage (from buckets) + AI (from aiLlms)
                        const allProviders: { type: 'storage' | 'ai'; provider: string }[] = [];
                        
                        // Storage providers from buckets
                        // API returns: buckets[].bucket.storageType (nested structure)
                        const buckets = repo.buckets || [];
                        const storageProviders = [...new Set(buckets.map((b: any) => b.bucket?.storageType || b.storageType).filter(Boolean))];
                        storageProviders.forEach((p: any) => allProviders.push({ type: 'storage', provider: p }));
                        
                        // AI providers from aiLlms
                        const aiLlms = (repo as any).aiLlms || [];
                        const aiProviders = [...new Set(aiLlms.map((llm: any) => (llm.aiLlm?.aiType || llm.aiLlm?.provider || llm.aiType || llm.provider || '').toUpperCase()).filter(Boolean))];
                        aiProviders.forEach((p: any) => allProviders.push({ type: 'ai', provider: p }));
                        
                        // Debug logging
                        console.log('[StorageRepos] Repo "' + repo.name + '" providers:', {
                          buckets: buckets.map((b: any) => ({ bucketId: b.bucketId, name: b.bucket?.name, storageType: b.bucket?.storageType })),
                          storageProviders,
                          aiLlms: aiLlms.map((l: any) => ({ aiLlmId: l.aiLlmId, name: l.aiLlm?.name, aiType: l.aiLlm?.aiType })),
                          aiProviders,
                          allProviders
                        });
                        
                        const displayProviders = allProviders.slice(0, 4);
                        const extraCount = allProviders.length - 4;
                        
                        const getProviderLogo = (item: { type: string; provider: string }) => {
                          const p = item.provider?.toUpperCase() || '';
                          if (item.type === 'storage') {
                            if (p === 'S3' || p === 'AWS') return logoAws;
                            if (p === 'GCS' || p === 'GOOGLE') return logoGoogle;
                            if (p === 'BLOB' || p === 'AZURE') return logoAzure;
                            if (p === 'STORJ') return logoStorj;
                            if (p === 'AKAVE') return logoAkave;
                            if (p === 'FILECOIN' || p === 'FIL') return logoFilecoin;
                            return logoOther;
                          }
                          if (item.type === 'ai') {
                            if (p === 'ANTHROPIC') return null; // use SVG
                            if (p === 'GOOGLE' || p === 'GEMINI') return logoGoogle;
                            if (p === 'AWS' || p === 'BEDROCK') return logoAws;
                          }
                          return null;
                        };
                        
                        return (
                          <>
                            {displayProviders.map((item, pIndex) => {
                              const logo = getProviderLogo(item);
                              const isOpenAI = item.type === 'ai' && item.provider === 'OPENAI';
                              const isAnthropic = item.type === 'ai' && item.provider === 'ANTHROPIC';
                              
                              return (
                                <div
                                  key={pIndex}
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    backgroundColor: '#FFFFFF',
                                    border: '2px solid #FFFFFF',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginLeft: pIndex > 0 ? '-8px' : 0,
                                    zIndex: displayProviders.length - pIndex,
                                    position: 'relative',
                                  }}
                                >
                                  {isOpenAI ? (
                                    <svg viewBox="0 0 24 24" style={{ width: 16, height: 16 }} fill="#10A37F">
                                      <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.8956zm16.5963 3.8558L13.1038 8.364l2.0201-1.1638a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.4046-.6813zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/>
                                    </svg>
                                  ) : isAnthropic ? (
                                    <svg viewBox="0 0 46 32" style={{ width: 16, height: 16 }} fill="#CC785C">
                                      <path d="M32.73 0h-6.945L38.45 32h6.945L32.73 0ZM13.27 0 0 32h7.082l2.72-7.273h13.945l2.72 7.273h7.082L20.28 0h-7.01Zm-.81 18.909 4.322-11.56 4.322 11.56H12.46Z"/>
                                    </svg>
                                  ) : logo ? (
                                    <img src={logo} alt="" style={{ width: 16, height: 16, objectFit: 'contain' }} />
                                  ) : null}
                                </div>
                              );
                            })}
                            {extraCount > 0 && (
                              <div
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  backgroundColor: '#F3F4F6',
                                  border: '2px solid #FFFFFF',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginLeft: '-8px',
                                  zIndex: 0,
                                }}
                              >
                                <Typography style={{ fontSize: '10px', fontWeight: 600, color: '#6B7280' }}>+{extraCount}</Typography>
                              </div>
                            )}
                            {allProviders.length === 0 && (
                              <Typography style={{ fontSize: '12px', color: '#9CA3AF' }}>-</Typography>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </td>

                  {/* Resources Column - number of buckets + AI LLMs */}
                  <td className="hidden lg:table-cell px-6 py-3 whitespace-nowrap">
                    <Typography variant="body1" style={{ color: '#717680' }}>
                      {(() => {
                        const buckets = repo.buckets.length;
                        const aiLlms = repo.aiLlms?.length || 0;
                        const total = buckets + aiLlms;
                        return total;
                      })()}
                    </Typography>
                  </td>

                  {/* Keys Column */}
                  <td className="hidden lg:table-cell px-6 py-3 whitespace-nowrap">
                    <Typography variant="body1" style={{ color: '#717680' }}>
                      {(() => {
                        const apiKeys = repo.apiKeys?.length || 0;
                        const aiKeys = repo.aiApiKeys?.length || 0;
                        const total = apiKeys + aiKeys;
                        return total;
                      })()}
                    </Typography>
                  </td>

                  {/* Support Column - Storage type + AI providers */}
                  <td className="hidden xl:table-cell px-6 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1 flex-wrap">
                      {(() => {
                        const badges: { label: string; bg: string; color: string; border: string }[] = [];
                        
                        // 1. Storage type badge (if repo has buckets)
                        if (repo.buckets?.length > 0 && repo.storageType) {
                          let label = '';
                          switch (repo.storageType) {
                            case ApiTypes.StorageType.S3:
                              label = 'S3';
                              break;
                            case ApiTypes.StorageType.GCS:
                              label = 'GCS';
                              break;
                            case ApiTypes.StorageType.BLOB:
                              label = 'Blob';
                              break;
                            default:
                              label = repo.storageType;
                          }
                          badges.push({ label, bg: '#F3F4F6', color: '#374151', border: '#E5E7EB' });
                        }
                        
                        // 2. AI provider badges (from aiApiKeys + aiLlms)
                        const aiTypes = repo.aiApiKeys?.map((key: any) => key.aiType).filter(Boolean) || [];
                        const aiLlmTypes = repo.aiLlms?.map((llm: any) => llm.aiType || llm.llmType).filter(Boolean) || [];
                        const uniqueAiTypes = Array.from(new Set([...aiTypes, ...aiLlmTypes]));
                        
                        const aiLabels: Record<string, string> = {
                          'OPENAI': 'OpenAI',
                          'ANTHROPIC': 'Anthropic',
                          'GOOGLE': 'Google AI',
                          'GEMINI': 'Google AI',
                          'AWS': 'AWS Bedrock',
                          'BEDROCK': 'AWS Bedrock',
                        };
                        const seenAi = new Set<string>();
                        uniqueAiTypes.forEach((aiType: string) => {
                          const label = aiLabels[aiType.toUpperCase()] || aiType;
                          if (!seenAi.has(label)) {
                            seenAi.add(label);
                            badges.push({ label, bg: '#F5F3FF', color: '#6D28D9', border: '#DDD6FE' });
                          }
                        });
                        
                        if (badges.length === 0) {
                          return <span style={{ color: '#9CA3AF' }}>-</span>;
                        }
                        
                        return badges.map((badge, idx) => (
                          <span
                            key={idx}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 8px',
                              borderRadius: '6px',
                              backgroundColor: badge.bg,
                              color: badge.color,
                              border: `1px solid ${badge.border}`,
                              fontSize: '11px',
                              fontWeight: 500,
                            }}
                          >
                            {badge.label}
                          </span>
                        ));
                      })()}
                    </div>
                  </td>

                  {/* Requests Column */}
                  <td className="hidden xl:table-cell px-6 py-3 whitespace-nowrap">
                    <Typography variant="body1" style={{ color: '#717680' }}>
                      {(repoStats && repoStats[repo.id]?.requests) || 0}
                    </Typography>
                  </td>

                  {/* Created Column */}
                  <td className="hidden xl:table-cell px-6 py-3 whitespace-nowrap">
                    <Typography variant="body1" style={{ color: '#717680', fontSize: '13px' }}>
                      {(() => {
                        const createdAt = (repo as any).createdAt || (repo as any).created_at || (repo as any).timestamp;
                        if (createdAt) {
                          const date = new Date(createdAt);
                          return date.toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          });
                        }
                        return '-';
                      })()}
                    </Typography>
                  </td>

                  <td className="px-6 py-3 whitespace-nowrap text-right">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuAnchorEl(e.currentTarget);
                        setMenuRepoId(repo.id);
                      }}
                      sx={{ 
                        color: '#667085', 
                        '&:hover': { bgcolor: '#F3F4F6' } 
                      }}
                      aria-label="More options"
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <Menu
                      anchorEl={menuRepoId === repo.id ? menuAnchorEl : null}
                      open={menuRepoId === repo.id && Boolean(menuAnchorEl)}
                      onClose={() => {
                        setMenuAnchorEl(null);
                        setMenuRepoId(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      PaperProps={{
                        sx: {
                          width: 180,
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                          mt: 0.5,
                        }
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          setMenuAnchorEl(null);
                          setMenuRepoId(null);
                          navigate(`/repositories/inventory/${repo.id}`);
                        }}
                        sx={{ py: 1.5, px: 2 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <img src={eyeSvg} alt="View" style={{ width: 18, height: 18 }} />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '14px', color: '#414651' } }}>
                          View
                        </ListItemText>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setMenuAnchorEl(null);
                          setMenuRepoId(null);
                          navigate(`/repositories/inventory/edit/${repo.id}`);
                        }}
                        sx={{ py: 1.5, px: 2 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <img src={settingsSvg} alt="Settings" style={{ width: 18, height: 18 }} />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '14px', color: '#414651' } }}>
                          Edit
                        </ListItemText>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setMenuAnchorEl(null);
                          setMenuRepoId(null);
                          const workspaceId = (repo as any).workspaceId || (repo as any).workspace_id || selectedWorkspaceId;
                          navigate(`/ai/policies?workspaceId=${workspaceId}&repoId=${repo.id}`);
                        }}
                        sx={{ py: 1.5, px: 2 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                          </svg>
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '14px', color: '#414651' } }}>
                          Manage Policies
                        </ListItemText>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setMenuAnchorEl(null);
                          setMenuRepoId(null);
                          handleDeleteRepo(repo.id);
                        }}
                        sx={{ py: 1.5, px: 2 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <img src={deleteSvg} alt="Delete" style={{ width: 18, height: 18 }} />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '14px', color: '#F04438' } }}>
                          Delete
                        </ListItemText>
                      </MenuItem>
                    </Menu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between py-6 px-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalFiltered)} of {totalFiltered} {searchQuery ? 'filtered ' : ''}repositories
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: currentPage === 1 ? '#E5E7EB' : '#617BFF',
                  color: currentPage === 1 ? '#9CA3AF' : 'white',
                }}
              >
                Previous
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className="px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                    style={{
                      backgroundColor: currentPage === page ? '#617BFF' : '#F3F4F6',
                      color: currentPage === page ? 'white' : '#374151',
                    }}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: currentPage === totalPages ? '#E5E7EB' : '#617BFF',
                  color: currentPage === totalPages ? '#9CA3AF' : 'white',
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* New/Edit Repo Dialog - DEPRECATED: Navigation to /storage-repos/add or /storage-repos/edit/:id instead */}
      <Transition appear show={openNewRepo} as={Fragment}>
        <HeadlessUIDialog
          as="div"
          className="relative z-100"
          onClose={() => {
            setOpenNewRepo(false);
            setSelectedRepo(null);
          }}
        >
          {/* Overlay avec flou */}
          <div className="fixed inset-0 bg-black bg-opacity-25 blur-background z-50" />

          {/* Contenu du dialogue sans flou */}
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <div className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all border-l-4 border-[#788CFF] no-blur">
                <HeadlessUIDialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  Add Repository
                </HeadlessUIDialog.Title>

                <div className="mt-4">
                  <Tabs
                    value={tabValue}
                    onChange={(_, newValue) => setTabValue(newValue)}
                    className="border-b border-gray-200"
                    sx={{
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#788CFF',
                      },
                      '& .MuiTab-root': {
                        color: '#6b7280',
                        '&.Mui-selected': {
                          color: '#788CFF',
                        },
                      },
                    }}
                  >
                    <Tab label="General" id="repo-tab-0" aria-controls="repo-tabpanel-0" />
                    <Tab label="Buckets" id="repo-tab-1" aria-controls="repo-tabpanel-1" />
                    <Tab label="API Keys" id="repo-tab-2" aria-controls="repo-tabpanel-2" />
                    <Tab label="AI LLMs" id="repo-tab-3" aria-controls="repo-tabpanel-3" />
                    <Tab label="AI API Keys" id="repo-tab-4" aria-controls="repo-tabpanel-4" />
                  </Tabs>

                  <TabPanel value={tabValue} index={0}>
                    {/* Contenu de l'onglet General */}
                    <div className="space-y-4">
                      <div>
                        <label
                          htmlFor="repo-name"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Repository Name
                        </label>
                        <TextField
                          id="repo-name"
                          fullWidth
                          size="small"
                          value={newRepo.name}
                          onChange={(e) => {
                            setNewRepo((prev) => ({ ...prev, name: e.target.value }));
                            setValidationErrors((prev) => ({ ...prev, name: undefined }));
                          }}
                          error={!!validationErrors.name}
                          helperText={validationErrors.name}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: validationErrors.name ? '#d32f2f' : '#e5e7eb',
                              },
                              '&:hover fieldset': {
                                borderColor: validationErrors.name ? '#d32f2f' : '#788CFF',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: validationErrors.name ? '#d32f2f' : '#788CFF',
                              },
                            },
                          }}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="storage-type"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          API endpoint type
                        </label>
                        <select
                          id="storage-type"
                          value={newRepo.storageType}
                          onChange={(e) => {
                            setNewRepo((prev) => ({
                              ...prev,
                              storageType: e.target.value as ApiTypes.StorageType,
                            }));
                          }}
                          className="mt-1 block w-full rounded-md bg-gray-800 border-gray-700 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        >
                          <option value={ApiTypes.StorageType.S3}>S3</option>
                          <option value={ApiTypes.StorageType.GCS}>GCS</option>
                          <option value={ApiTypes.StorageType.BLOB}>Azure Blob</option>
                        </select>
                      </div>

                      <div>
                        <label
                          htmlFor="mode-type"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Mode
                        </label>
                        <TextField
                          id="mode-type"
                          select
                          fullWidth
                          size="small"
                          value={newRepo.mode}
                          onChange={(e) => {
                            setNewRepo((prev) => ({
                              ...prev,
                              mode: e.target.value as ApiTypes.ModeType,
                            }));
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: '#e5e7eb',
                              },
                              '&:hover fieldset': {
                                borderColor: '#788CFF',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#788CFF',
                              },
                            },
                          }}
                        >
                          <option value={ApiTypes.ModeType.NORMAL}>Normal</option>
                          {/* Commenté temporairement car REDUNDANT n'existe pas dans ModeType */}
                          {/* <option value={ApiTypes.ModeType.REDUNDANT}>Redundant</option> */}
                        </TextField>
                      </div>
                    </div>
                  </TabPanel>

                  <TabPanel value={tabValue} index={1}>
                    {/* Contenu de l'onglet Buckets */}
                    <div className="space-y-4">
                      {availableUnits.length === 0 ? (
                        <div className="text-gray-500 text-center py-4">
                          No storage buckets available. Please create a storage bucket first.
                        </div>
                      ) : (
                        <>
                          {newRepo.repoUnits.length > 0 ? (
                            <>
                              <div className="mb-4">
                                <Typography variant="subtitle2" className="text-gray-700 mb-2">
                                  Selected Buckets ({newRepo.repoUnits.length})
                                </Typography>
                              </div>
                              <div className="flex flex-col space-y-4">
                                {newRepo.repoUnits.map((unit, index) => (
                                  <div
                                    key={unit.unitId}
                                    className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg bg-gray-50"
                                  >
                                    <div className="flex-1 space-y-2">
                                      <div>
                                        <Typography variant="body1" className="font-medium">
                                          {availableUnits.find((u) => u.id === unit.unitId)?.name ||
                                            'Unknown Unit'}
                                        </Typography>
                                        <Typography variant="body2" className="text-gray-500">
                                          {availableUnits.find((u) => u.id === unit.unitId)
                                            ?.bucket || 'Unknown Bucket'}
                                        </Typography>
                                      </div>
                                      <div>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label="Folder Path"
                                          placeholder="e.g., backups, data"
                                          value={unit.folder || ''}
                                          onChange={(e) => {
                                            const updatedUnits = [...newRepo.repoUnits];
                                            updatedUnits[index] = {
                                              ...unit,
                                              folder: e.target.value,
                                            };
                                            setNewRepo((prev) => ({
                                              ...prev,
                                              repoUnits: updatedUnits,
                                            }));
                                          }}
                                          sx={{
                                            '& .MuiOutlinedInput-root': {
                                              backgroundColor: 'white',
                                              '& fieldset': {
                                                borderColor: '#e5e7eb',
                                              },
                                              '&:hover fieldset': {
                                                borderColor: '#788CFF',
                                              },
                                              '&.Mui-focused fieldset': {
                                                borderColor: '#788CFF',
                                              },
                                            },
                                          }}
                                        />
                                      </div>
                                    </div>
                                    <div className="flex items-center">
                                      <Tooltip title="Master Unit">
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const updatedUnits = [...newRepo.repoUnits].map(
                                              (u) => ({
                                                ...u,
                                                master: u.unitId === unit.unitId,
                                              })
                                            );
                                            setNewRepo((prev) => ({
                                              ...prev,
                                              repoUnits: updatedUnits,
                                            }));
                                          }}
                                        >
                                          <div
                                            className={`w-4 h-4 rounded-full ${
                                              unit.master
                                                ? 'bg-green-500 ring-2 ring-green-300'
                                                : 'bg-gray-300'
                                            }`}
                                          />
                                        </IconButton>
                                      </Tooltip>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const updatedUnits = newRepo.repoUnits.filter(
                                            (u) => u.unitId !== unit.unitId
                                          );
                                          // If we removed the master, set a new one if possible
                                          if (unit.master && updatedUnits.length > 0) {
                                            updatedUnits[0].master = true;
                                          }
                                          setNewRepo((prev) => ({
                                            ...prev,
                                            repoUnits: updatedUnits,
                                          }));
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" className="text-gray-500" />
                                      </IconButton>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <Typography variant="body2">
                                No buckets added yet. Select buckets below to add them to this
                                repository.
                              </Typography>
                            </div>
                          )}

                          <div className="pt-4 border-t border-gray-200">
                            <Typography variant="subtitle2" className="text-gray-700 mb-3">
                              {newRepo.repoUnits.length > 0 ? 'Add More Buckets' : 'Add Buckets'}
                            </Typography>
                            <Autocomplete
                              options={availableUnits.filter(
                                (unit) => !newRepo.repoUnits.some((ru) => ru.unitId === unit.id)
                              )}
                              getOptionLabel={(option) => `${option.name} (${option.bucket})`}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Select a bucket to add"
                                  placeholder="Search buckets..."
                                  size="small"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      '& fieldset': {
                                        borderColor: '#e5e7eb',
                                      },
                                      '&:hover fieldset': {
                                        borderColor: '#788CFF',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#788CFF',
                                      },
                                    },
                                  }}
                                />
                              )}
                              onChange={(_, value) => {
                                if (value) {
                                  const isMaster = newRepo.repoUnits.length === 0;
                                  // Utiliser le nom du bucket comme dossier par défaut
                                  const defaultFolder = value.bucket
                                    ? value.bucket.toLowerCase().replace(/[^a-z0-9]/g, '-')
                                    : 'default';
                                  setNewRepo((prev) => ({
                                    ...prev,
                                    repoUnits: [
                                      ...prev.repoUnits,
                                      {
                                        unitId: value.id,
                                        master: isMaster,
                                        folder: defaultFolder, // Utiliser un dossier par défaut au lieu d'une chaîne vide
                                      } as RepoUnitInfo,
                                    ],
                                  }));
                                }
                              }}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              noOptionsText="No available units of this type"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </TabPanel>

                  <TabPanel value={tabValue} index={2}>
                    {/* Contenu de l'onglet API Keys */}
                    <div className="space-y-4">
                      <div className="flex justify-end">
                        <StyledButton
                          text="Add API Key"
                          onClick={() => {
                            if (!selectedRepo) return;
                            handleCreateApiKey(selectedRepo.id);
                          }}
                          icon={StyledButtonIcon.Plus}
                          variant="dark"
                        />
                      </div>

                      <div className="space-y-4 max-h-96 overflow-y-auto">
                        {selectedRepo?.apiKeys?.map((apiKey) => (
                          <div
                            key={apiKey.id}
                            className="bg-white rounded-lg p-4 space-y-2 border border-gray-200"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                {editingApiKey?.id === apiKey.id ? (
                                  <div className="space-y-2">
                                    <TextField
                                      fullWidth
                                      size="small"
                                      value={editingApiKey.name}
                                      onChange={(e) =>
                                        setEditingApiKey({
                                          ...editingApiKey,
                                          name: e.target.value,
                                        })
                                      }
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          '& fieldset': {
                                            borderColor: '#e5e7eb',
                                          },
                                          '&:hover fieldset': {
                                            borderColor: '#788CFF',
                                          },
                                          '&.Mui-focused fieldset': {
                                            borderColor: '#788CFF',
                                          },
                                        },
                                      }}
                                    />
                                    <TextField
                                      select
                                      fullWidth
                                      size="small"
                                      value={editingApiKey.accessType}
                                      onChange={(e) =>
                                        setEditingApiKey({
                                          ...editingApiKey,
                                          accessType: e.target.value as ApiTypes.AccessType,
                                        })
                                      }
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          '& fieldset': {
                                            borderColor: '#e5e7eb',
                                          },
                                          '&:hover fieldset': {
                                            borderColor: '#788CFF',
                                          },
                                          '&.Mui-focused fieldset': {
                                            borderColor: '#788CFF',
                                          },
                                        },
                                      }}
                                    >
                                      {Object.values(ApiTypes.AccessType).map((type) => (
                                        <option key={type} value={type}>
                                          {type}
                                        </option>
                                      ))}
                                    </TextField>
                                    <div className="flex space-x-2">
                                      <StyledButton
                                        text="Save"
                                        onClick={() => {
                                          if (selectedRepo) {
                                            handleUpdateApiKey(
                                              selectedRepo.id,
                                              editingApiKey.id,
                                              editingApiKey.name,
                                              editingApiKey.accessType
                                            );
                                          }
                                        }}
                                        icon={StyledButtonIcon.RefreshCw}
                                        variant="dark"
                                      />
                                      <StyledButton
                                        text="Cancel"
                                        onClick={() => setEditingApiKey(null)}
                                        icon={StyledButtonIcon.X}
                                        variant="dark"
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <Typography variant="subtitle1" className="text-gray-900">
                                      {apiKey.name}
                                    </Typography>
                                    <Typography variant="body2" className="text-gray-500">
                                      {apiKey.accessType}
                                    </Typography>
                                  </>
                                )}
                              </div>
                              {editingApiKey?.id !== apiKey.id && (
                                <div className="flex space-x-2">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditingApiKey({
                                        id: apiKey.id,
                                        name: apiKey.name,
                                        accessType: apiKey.accessType,
                                        key: apiKey.key,
                                        secret: apiKey.secret?.key || '',
                                      });
                                    }}
                                    className="text-gray-500 hover:text-[#788CFF]"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      selectedRepo && handleDeleteApiKey(selectedRepo.id, apiKey.id)
                                    }
                                    className="text-gray-500 hover:text-[#EBBDA3]"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </div>
                              )}
                            </div>
                            <div>
                              <Typography variant="body2" className="text-gray-500">
                                API Key
                              </Typography>
                              <div className="flex items-center space-x-2">
                                <Typography variant="body1" className="text-gray-900">
                                  {apiKey.key}
                                </Typography>
                                <Tooltip title="Copy">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCopy(apiKey.key)}
                                    className="text-gray-500 hover:text-[#788CFF]"
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            </div>
                            <div>
                              <Typography variant="body2" className="text-gray-500">
                                Secret
                              </Typography>
                              <div className="flex items-center space-x-2">
                                <Typography variant="body1" className="text-gray-900">
                                  {showSecret[apiKey.id]
                                    ? apiKey.secret?.key
                                    : '••••••••••••••••' + apiKey.secret?.key.slice(-4)}
                                </Typography>
                                <Tooltip title={showSecret[apiKey.id] ? 'Hide' : 'Show'}>
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleSecretVisibility(apiKey.id)}
                                    className="text-gray-500 hover:text-[#788CFF]"
                                  >
                                    {showSecret[apiKey.id] ? (
                                      <VisibilityOffIcon fontSize="small" />
                                    ) : (
                                      <VisibilityIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Copy">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCopy(apiKey.secret?.key || '')}
                                    className="text-gray-500 hover:text-[#788CFF]"
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            </div>
                            {apiKey.createdAt && (
                              <div>
                                <Typography variant="body2" className="text-gray-500">
                                  Created
                                </Typography>
                                <Typography variant="body1" className="text-gray-900">
                                  {new Date(apiKey.createdAt).toLocaleString()}
                                </Typography>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </TabPanel>

                  <TabPanel value={tabValue} index={3}>
                    {/* Contenu de l'onglet AI LLMs */}
                    <div className="space-y-4">
                      {selectedRepo?.aiLlms && selectedRepo.aiLlms.length > 0 ? (
                        <div className="space-y-3">
                          {selectedRepo.aiLlms.map((aiLlmAssoc: any) => {
                            const aiLlm = aiLlmAssoc.aiLlm;
                            if (!aiLlm) return null;
                            
                            return (
                              <div
                                key={aiLlmAssoc.aiLlmId}
                                className="bg-white rounded-lg p-4 border border-gray-200"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <Typography variant="h6" style={{ fontSize: '16px', fontWeight: 600, color: '#101828', marginBottom: '4px' }}>
                                      {aiLlm.name}
                                    </Typography>
                                    <div className="space-y-2 mt-2">
                                      <div>
                                        <Typography variant="body2" style={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>
                                          Type
                                        </Typography>
                                        <Typography variant="body1" style={{ fontSize: '14px', color: '#101828' }}>
                                          {aiLlm.aiType}
                                        </Typography>
                                      </div>
                                      {aiLlm.endpoint && (
                                        <div>
                                          <Typography variant="body2" style={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>
                                            Endpoint
                                          </Typography>
                                          <Typography variant="body1" style={{ fontSize: '14px', color: '#101828', wordBreak: 'break-all' }}>
                                            {aiLlm.endpoint}
                                          </Typography>
                                        </div>
                                      )}
                                      {aiLlm.createdAt && (
                                        <div>
                                          <Typography variant="body2" style={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>
                                            Created
                                          </Typography>
                                          <Typography variant="body1" style={{ fontSize: '14px', color: '#101828' }}>
                                            {new Date(aiLlm.createdAt).toLocaleString()}
                                          </Typography>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <div 
                            className="inline-block p-6 rounded-lg border"
                            style={{ 
                              backgroundColor: '#F9FAFB',
                              borderColor: '#E5E7EB',
                            }}
                          >
                            <Typography style={{ color: '#667085', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
                              No AI LLMs associated
                            </Typography>
                            <Typography style={{ color: '#9CA3AF', fontSize: '13px' }}>
                              This repository has no AI LLM configurations associated with it yet.
                            </Typography>
                          </div>
                        </div>
                      )}
                    </div>
                  </TabPanel>

                  <TabPanel value={tabValue} index={4}>
                    {/* Contenu de l'onglet AI API Keys */}
                    <div className="space-y-4">
                      {selectedRepo?.aiApiKeys && selectedRepo.aiApiKeys.length > 0 ? (
                        <div className="space-y-3">
                          {selectedRepo.aiApiKeys.map((aiApiKey: any) => (
                            <div
                              key={aiApiKey.id}
                              className="bg-white rounded-lg p-4 border border-gray-200"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <Typography variant="h6" style={{ fontSize: '16px', fontWeight: 600, color: '#101828', marginBottom: '4px' }}>
                                    {aiApiKey.name}
                                  </Typography>
                                  <div className="space-y-2 mt-2">
                                    <div>
                                      <Typography variant="body2" style={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>
                                        AI Type
                                      </Typography>
                                      <Typography variant="body1" style={{ fontSize: '14px', color: '#101828' }}>
                                        {aiApiKey.aiType}
                                      </Typography>
                                    </div>
                                    {aiApiKey.key && (
                                      <div>
                                        <Typography variant="body2" style={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>
                                          Key
                                        </Typography>
                                        <div className="flex items-center gap-2">
                                          <Typography variant="body1" style={{ fontSize: '14px', color: '#101828' }}>
                                            {aiApiKey.key}
                                          </Typography>
                                          <Tooltip title="Copy">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleCopy(aiApiKey.key)}
                                              className="text-gray-500 hover:text-[#788CFF]"
                                            >
                                              <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </div>
                                      </div>
                                    )}
                                    {aiApiKey.createdAt && (
                                      <div>
                                        <Typography variant="body2" style={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>
                                          Created
                                        </Typography>
                                        <Typography variant="body1" style={{ fontSize: '14px', color: '#101828' }}>
                                          {new Date(aiApiKey.createdAt).toLocaleString()}
                                        </Typography>
                                      </div>
                                    )}
                                    {aiApiKey.deletedAt && (
                                      <div>
                                        <Typography variant="body2" style={{ fontSize: '12px', color: '#F04438', fontWeight: 500 }}>
                                          Deleted
                                        </Typography>
                                        <Typography variant="body1" style={{ fontSize: '14px', color: '#F04438' }}>
                                          {new Date(aiApiKey.deletedAt).toLocaleString()}
                                        </Typography>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <div 
                            className="inline-block p-6 rounded-lg border"
                            style={{ 
                              backgroundColor: '#F9FAFB',
                              borderColor: '#E5E7EB',
                            }}
                          >
                            <Typography style={{ color: '#667085', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
                              No AI API Keys
                            </Typography>
                            <Typography style={{ color: '#9CA3AF', fontSize: '13px' }}>
                              This repository has no AI API keys configured yet.
                            </Typography>
                          </div>
                        </div>
                      )}
                    </div>
                  </TabPanel>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <StyledButton
                    text="Cancel"
                    onClick={() => {
                      setOpenNewRepo(false);
                      setSelectedRepo(null);
                    }}
                    icon={StyledButtonIcon.X}
                    variant="dark"
                  />
                  <StyledButton
                    text="Create"
                    onClick={handleCreateRepo}
                    icon={StyledButtonIcon.Plus}
                    variant="dark"
                  />
                </div>
              </div>
            </div>
          </div>
        </HeadlessUIDialog>
      </Transition>

      {/* API Keys Dialog */}
      <Transition appear show={openApiKeys} as={Fragment}>
        <HeadlessUIDialog
          as="div"
          className="relative z-10"
          onClose={() => {
            setOpenApiKeys(false);
            setSelectedRepo(null);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessUIDialog.Panel
                  className="w-full max-w-md transform overflow-hidden rounded-2xl bg-[#1a1b1e] p-6 text-left align-middle shadow-xl transition-all"
                  style={{ borderLeft: '4px solid #EBBDA3' }}
                >
                  <HeadlessUIDialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-white"
                  >
                    API Keys for {selectedRepo?.name}
                  </HeadlessUIDialog.Title>
                  <div className="mt-4 space-y-4">
                    <div className="flex justify-end">
                      <StyledButton
                        text="Add API Key"
                        onClick={() => {
                          if (!selectedRepo) return;
                          handleCreateApiKey(selectedRepo.id);
                        }}
                        icon={StyledButtonIcon.Plus}
                      />
                    </div>

                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {selectedRepo?.apiKeys?.map((apiKey) => (
                        <div key={apiKey.id} className="bg-gray-800 rounded-lg p-4 space-y-2">
                          <div className="flex justify-between items-start">
                            <div>
                              {editingApiKey?.id === apiKey.id ? (
                                <div className="space-y-2">
                                  <input
                                    type="text"
                                    value={editingApiKey.name}
                                    onChange={(e) =>
                                      setEditingApiKey({
                                        ...editingApiKey,
                                        name: e.target.value,
                                      })
                                    }
                                    className="block w-full rounded-md bg-gray-700 border-gray-600 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                  />
                                  <select
                                    value={editingApiKey.accessType}
                                    onChange={(e) =>
                                      setEditingApiKey({
                                        ...editingApiKey,
                                        accessType: e.target.value as ApiTypes.AccessType,
                                      })
                                    }
                                    className="block w-full rounded-md bg-gray-700 border-gray-600 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                  >
                                    {Object.values(ApiTypes.AccessType).map((type) => (
                                      <option key={type} value={type}>
                                        {type}
                                      </option>
                                    ))}
                                  </select>
                                  <div className="flex space-x-2">
                                    <StyledButton
                                      text="Save"
                                      onClick={() => {
                                        if (selectedRepo) {
                                          handleUpdateApiKey(
                                            selectedRepo.id,
                                            editingApiKey.id,
                                            editingApiKey.name,
                                            editingApiKey.accessType
                                          );
                                        }
                                      }}
                                      icon={StyledButtonIcon.RefreshCw}
                                    />
                                    <StyledButton
                                      text="Cancel"
                                      onClick={() => setEditingApiKey(null)}
                                      icon={StyledButtonIcon.X}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <Typography variant="subtitle1" className="text-white">
                                    {apiKey.name}
                                  </Typography>
                                  <Typography variant="body2" className="text-gray-400">
                                    {apiKey.accessType}
                                  </Typography>
                                </>
                              )}
                            </div>
                            {editingApiKey?.id !== apiKey.id && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  selectedRepo && handleDeleteApiKey(selectedRepo.id, apiKey.id)
                                }
                                className="text-gray-400 hover:text-red-500"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </div>
                          <div>
                            <Typography variant="body2" className="text-gray-400">
                              API Key
                            </Typography>
                            <div className="flex items-center space-x-2">
                              <Typography variant="body1" className="text-white">
                                {apiKey.key}
                              </Typography>
                              <Tooltip title="Copy">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopy(apiKey.key)}
                                  className="text-gray-400 hover:text-white"
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>
                          <div>
                            <Typography variant="body2" className="text-gray-400">
                              Secret
                            </Typography>
                            <div className="flex items-center space-x-2">
                              <Typography variant="body1" className="text-white">
                                {showSecret[apiKey.id]
                                  ? apiKey.secret?.key
                                  : '••••••••••••••••' + apiKey.secret?.key.slice(-4)}
                              </Typography>
                              <Tooltip title={showSecret[apiKey.id] ? 'Hide' : 'Show'}>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleSecretVisibility(apiKey.id)}
                                  className="text-gray-400 hover:text-white"
                                >
                                  {showSecret[apiKey.id] ? (
                                    <VisibilityOffIcon fontSize="small" />
                                  ) : (
                                    <VisibilityIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopy(apiKey.secret?.key || '')}
                                  className="text-gray-400 hover:text-white"
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>
                          {apiKey.createdAt && (
                            <div>
                              <Typography variant="body2" className="text-gray-400">
                                Created
                              </Typography>
                              <Typography variant="body1" className="text-white">
                                {new Date(apiKey.createdAt).toLocaleString()}
                              </Typography>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end">
                    <StyledButton
                      text="Close"
                      onClick={() => {
                        setOpenApiKeys(false);
                        setSelectedRepo(null);
                      }}
                      icon={StyledButtonIcon.X}
                    />
                  </div>
                </HeadlessUIDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessUIDialog>
      </Transition>

      {/* Buckets Popover */}
      <Popover
        open={Boolean(bucketsPopoverAnchor)}
        anchorEl={bucketsPopoverAnchor?.element}
        onClose={() => setBucketsPopoverAnchor(null)}
        disableRestoreFocus
        sx={{
          pointerEvents: 'none',
        }}
        PaperProps={{
          onMouseEnter: () => {
            // Keep popover open when hovering over it
          },
          onMouseLeave: () => setBucketsPopoverAnchor(null),
          sx: {
            pointerEvents: 'auto',
            borderRadius: '12px',
            boxShadow: '0px 12px 24px rgba(16, 24, 40, 0.12)',
            border: '1px solid #E9EAEB',
            mt: 1,
            maxWidth: '320px',
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              color: '#181D27',
              mb: 1.5,
            }}
          >
            Buckets ({bucketsPopoverAnchor?.repo.buckets.length || 0})
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {bucketsPopoverAnchor?.repo.buckets.map((bucket: any, index: number) => {
              // Get bucket name from bucket object
              const bucketName = bucket.bucket?.name || bucket.name || `Bucket ${index + 1}`;
              return (
                <Box
                  key={bucket.bucketId || index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '8px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                  }}
                >
                  <Box
                    sx={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: '#617BFF',
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#181D27',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {bucketName}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Popover>
      </div>
    </div>
  );
}

export default StorageRepos;
